import { Button, Checkbox, Textarea, Field } from "@fluentui/react-components";
import { PropsWithChildren } from "react";
import { Feedback } from "../../../api";
import styles from "./FeedbackModal.module.css";

type BaseProps = {
  open: boolean;
  onClose: () => void;
};

type NegativeProps = BaseProps & {
  negativeFeedbackList: Feedback[];
  updateFeedbackList: (
    ev: React.FormEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  feedbackText: string;
  setFeedbackText: (value: string) => void;
  onSubmit: () => void;
};

type PositiveProps = BaseProps & {
  onSubmit: () => void;
};

// Renamed to InternalModalShell to avoid confusion
// and fixed the 'children' type error
const InternalModalShell: React.FC<
  PropsWithChildren<BaseProps & { title: string; subtitle: string }>
> = ({ open, onClose, title, subtitle, children }) => {
  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  );
};

export const PositiveFeedbackModal: React.FC<PositiveProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  return (
    <InternalModalShell
      open={open}
      onClose={onClose}
      title="Submit Feedback"
      subtitle="Your feedback will improve this experience."
    >
      <div>Thank you for your feedback.</div>
      <div className={styles.footer}>
        <Button appearance="primary" onClick={onSubmit}>
          Submit
        </Button>
        <Button appearance="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </InternalModalShell>
  );
};

export const NegativeFeedbackModal: React.FC<NegativeProps> = ({
  open,
  onClose,
  negativeFeedbackList,
  updateFeedbackList,
  feedbackText,
  setFeedbackText,
  onSubmit,
}) => {
  // Mapping API Enum values to user-friendly labels
  const feedbackOptions = [
    { id: Feedback.MissingCitation, label: "Citations are missing" },
    { id: Feedback.OutOfScope, label: "Response is out of scope" },
    {
      id: Feedback.InaccurateOrIrrelevant,
      label: "Information is inaccurate or irrelevant",
    },
    { id: Feedback.SlowAppPerformance, label: "App performance is slow" },
    { id: Feedback.OtherUnhelpful, label: "Other" },
  ];

  return (
    <InternalModalShell
      open={open}
      onClose={onClose}
      title="Submit Feedback"
      subtitle="Your feedback will help us improve."
    >
      <div className={styles.sectionLabel}>
        Why wasn't this response helpful?
      </div>

      <div className={styles.checkboxGroup}>
        {feedbackOptions.map((option) => (
          <Checkbox
            key={option.id}
            label={option.label}
            id={option.id}
            checked={negativeFeedbackList.includes(option.id)}
            onChange={(ev, data) =>
              updateFeedbackList(ev as any, !!data.checked)
            }
          />
        ))}
      </div>

      <div className={styles.textareaBlock}>
        <label htmlFor="additional-feedback" className={styles.sectionLabel}>
          Additional feedback
        </label>
        <Textarea
          id="additional-feedback"
          className={styles.textarea}
          value={feedbackText}
          onChange={(_, data) => setFeedbackText(data.value)}
          placeholder="Provide any additional details..."
          appearance="outline"
        />
      </div>

      <div className={styles.footer}>
        <Button appearance="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          appearance="primary"
          disabled={negativeFeedbackList.length === 0 && !feedbackText.trim()}
          onClick={onSubmit}
        >
          Submit
        </Button>
      </div>
    </InternalModalShell>
  );
};
